import bowser from 'bowser'

export default {

  props: {
    value: {},
    items: { type: Array, required: true },
    itemValueProp: { type: [String, Function] }, // a property on the item used to derive it's value when selected
    itemLabelProp: { type: [String, Function] }, // a property on the item used to derive it's display text
    itemKeyProp: { type: String }, // a property on each item in items that is used to uniquely identify each item
    required: { type: Boolean },
    multiple: { type: Boolean },
    toggle: {
      type: Boolean,
      default: function () {
        return this.multiple
      },
    },
    useNativeSelect: { type: Boolean, default:() => bowser.mobile || bowser.tablet },
    placeholder: { type: String, default: 'Search' },
    selectedItemLabel: {
      type: Function,
      default: function(selectedItem) { return this.getItemLabel(selectedItem) }
    },
    filterPredicate: {
      type: Function,
      default: function(list, query) {
        return list.filter(item => item && this.getItemLabel(item).toLowerCase().includes(query))
      }
    },
  },

  data() {
    return {
      selected: this.value,
      search_filter: '',
      isActive: false,
      keyboardFocusIdx: -1,
    }
  },

  computed: {

    formControlDisplayValue: {
      get() {
        if(this.isActive)
          return this.search_filter
        else if(this.hasSelection)
          return this.multiple ? `${this.selected.length} Items` : this.selectedItemLabel(this.selected)
        else
          return null
      },
      set(val) {
        this.search_filter = val
      },
    },

    // Multiple mode
    _selectedItems() {
      return this.hasSelection
        ? this.items.filter(item => this.itemIsSelected(item))
        : []
    },

    // Single mode
    _selectedItem() {
      return this.hasSelection
        ? this.getValueFromItem(this.selected)
        : null
    },

    searchResults() {
      return this.filterPredicate(this.items, this.search_filter.toLowerCase())
    },

    single() {
      return !this.multiple
    },

    hasSelection() {
      return this.selected != null
        ? this.multiple ? this.selected.length > 0 : true
        : false
    },

    isRequired() {
      return this.required ? !this.hasSelection : false
    },
  },

  watch: {
    value (items) {
      this.selected = items
    },

    selected (items) {
      this.$emit('input', items)
    },

    isActive (newVal) {
      this.search_filter = ''

      if(newVal == true)
        document.addEventListener('keydown', this.onKeyboardInput)
      else
        document.removeEventListener('keydown', this.onKeyboardInput)
      
      if(newVal == true && this.single && this.hasSelection) {
        this.$nextTick(() => {
          let item = this._selectedItem
          this.keyboardFocusIdx = this.searchResults.indexOf(item)
          this.scrollSearchResultIntoView(item, true)
        })
      }
    },

    search_filter() {
      this.keyboardFocusIdx = -1
      if(this.$refs.searchResultsParent) this.$refs.searchResultsParent.$el.scrollTop = 0
    }
  },

  methods: {
    itemIsSelected(item) {
      return this.single
        ? this.getItemIdentifier(this._selectedItem) == this.getItemIdentifier(item)
        : this.getCorrespondingSelectedItemFromAvailableItem(item) != null
    },

    getValueFromItem(item) {
      return this.itemValueProp == null
        ? item
        : typeof this.itemValueProp == 'function' ? this.itemValueProp(item) : item[this.itemValueProp]
    },

    getItemLabel(item) {
      return String(
        this.itemLabelProp == null
          ? item
          : typeof this.itemLabelProp == 'function' ? this.itemLabelProp(item) : item[this.itemLabelProp]
      )
    },

    getItemIdentifier(item) {
      return this.itemKeyProp == null ? item : item[this.itemKeyProp]
    },

    select(item) {
      let val = this.getValueFromItem(item)

      if(this.multiple) {
        if(this.selected == null) this.selected = []

        let isSelected = this.itemIsSelected(item)

        if(isSelected && this.toggle)
          this.deselect(item)
        else if(!isSelected)
          this.selected.push(val)
      }
      else {
        this.selected = this.selected != val || !this.toggle // set or toggle selection 
          ? val
          : null
        this.isActive = false
      }
    },

    deselect(item) {
      if(this.multiple) {
        let match = this.getCorrespondingSelectedItemFromAvailableItem(item)
        this.selected = this.selected.filter(s => s != match)
      }
      else {
        this.selected = null
      }
    },

    /**
     * Using an item from `this.items` as the search input, find the corresponding value in `this.selected`.
     * `this.selected` may contain values which have undergone a transformation via the `itemValueProp` property,
     * so this lookup is non-trivial
     */
    getCorrespondingSelectedItemFromAvailableItem(item) {
      return this.selected.find(selectedItem => {
        let selectedItemIsObject = typeof selectedItem == 'object'
        if(selectedItemIsObject && this.itemKeyProp != null) {
          return this.getItemIdentifier(selectedItem) == this.getItemIdentifier(item)
        }
        else if(selectedItemIsObject && this.itemKeyProp == null) {
          return JSON.stringify(selectedItem) == JSON.stringify(item)
        }
        else {
          return selectedItem == this.getValueFromItem(item)
        }
      })
    },

    scrollSearchResultIntoView(item, center = false) {
      if(this.searchResults.length == 0) return

      let idx = this.searchResults.indexOf(item)
      if(idx == -1) return
      let el = this.$refs.searchResults[idx]
      if(!el) return
      let parent = el.parentNode
      
      if(el.scrollIntoViewIfNeeded)
        el.scrollIntoViewIfNeeded(center)
      else
        parent.scrollTop = this.$refs.searchResults[idx].offsetTop - (parent.offsetHeight - el.offsetHeight) / 2
    },

    onKeyboardInput(e) {
      switch(e.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          if(e.key == 'ArrowUp')
            this.keyboardFocusIdx--
          else
            this.keyboardFocusIdx++

          // Wrap focus
          if(this.keyboardFocusIdx < 0) this.keyboardFocusIdx = this.searchResults.length - 1
          else if(this.keyboardFocusIdx >= this.searchResults.length) this.keyboardFocusIdx = 0

          // Scroll focused search item into view
          this.scrollSearchResultIntoView(this.searchResults[this.keyboardFocusIdx])
          e.preventDefault()
          break

        case 'Enter':
          if(this.keyboardFocusIdx !== -1)
            this.select(this.searchResults[this.keyboardFocusIdx])          
          e.preventDefault()
          break

        case 'Escape':
          this.isActive = false
          document.activeElement.blur()
          e.preventDefault()
          break
      }
    },
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeyboardInput)
  }
}